import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Icon, type IconName } from '@/lib/icons';
import { cn } from '@/lib/utils';

type Action = { label: string; onClick: () => void; primary?: boolean };

/**
 * A plain state inside the task frame (no question to show): loading error,
 * empty sector. Composed here: the kit has no empty / error state block.
 */
export function InitialDataState({
  icon,
  tone = 'default',
  title,
  body,
  actions = [],
  testId,
}: {
  icon: IconName;
  tone?: 'default' | 'danger';
  title: string;
  body?: ReactNode;
  actions?: Action[];
  testId: string;
}) {
  return (
    <section data-testid={testId} className="flex flex-col items-center rounded-xl border border-border/60 bg-surface px-5 py-12 text-center sm:px-6">
      <span
        className={cn(
          'grid size-12 place-items-center rounded-full',
          tone === 'danger' ? 'bg-danger/10 text-danger' : 'bg-surface-2 text-muted-foreground',
        )}
      >
        <Icon name={icon} className="size-5" />
      </span>
      <h2 className="mt-4 text-lg font-semibold tracking-tight text-foreground">{title}</h2>
      {body ? <p className="mt-2 max-w-md text-sm leading-6 text-muted-foreground">{body}</p> : null}
      {actions.length > 0 ? (
        <div className="mt-6 flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
          {actions.map((action) => (
            <Button key={action.label} variant={action.primary ? 'default' : 'outline'} className="h-11 sm:min-w-44" onClick={action.onClick}>
              {action.label}
            </Button>
          ))}
        </div>
      ) : null}
    </section>
  );
}
